import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { useTeamMutations } from '../hooks';
import type { Matter } from '../types';

interface ReassignPartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  onSuccess?: () => void;
}

// Case 360° (Stage 14) — Partner/MD swaps the matter Partner (Accountable).
export function ReassignPartnerDialog({
  open,
  onOpenChange,
  matter,
  onSuccess,
}: ReassignPartnerDialogProps) {
  const { t } = useTranslation();
  const [fromUserId, setFromUserId] = React.useState<number | undefined>(undefined);
  const [toUserId, setToUserId] = React.useState<number | undefined>(undefined);
  const { reassignPartnerMutation } = useTeamMutations();

  const partners = matter?.partners ?? [];
  const needsOutgoingChoice = partners.length > 1;

  React.useEffect(() => {
    if (open) {
      setFromUserId(partners.length === 1 ? partners[0]?.id : undefined);
      setToUserId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = async () => {
    if (!matter || !toUserId) return;
    if (needsOutgoingChoice && !fromUserId) return;
    await reassignPartnerMutation.mutateAsync({
      id: matter.id,
      fromUserId,
      toUserId,
    });
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('matterTeam.reassignPartnerTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('matterTeam.reassignPartnerDescription')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-4 space-y-4">
          {needsOutgoingChoice && (
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('matterTeam.outgoingPartner')}
              </label>
              <Select
                value={fromUserId ? String(fromUserId) : undefined}
                onValueChange={(v) => setFromUserId(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('matterTeam.outgoingPartner')} />
                </SelectTrigger>
                <SelectContent>
                  {partners.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('matterTeam.newPartner')}
            </label>
            <UniversalCombobox
              url="/users"
              listUrl="/users/select-box"
              labelKey="name"
              valueKey="id"
              selectedId={toUserId}
              params={{ status: 'ACTIVE', role_type: 'PARTNER' }}
              onSelectItem={(item) =>
                setToUserId(item?.['id'] as number | undefined)
              }
              placeholder={t('matterTeam.selectNewPartner')}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={reassignPartnerMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              !toUserId ||
              (needsOutgoingChoice && !fromUserId) ||
              reassignPartnerMutation.isPending
            }
          >
            {reassignPartnerMutation.isPending
              ? t('processing')
              : t('matterTeam.reassignPartnerConfirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
